import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import { alpha } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { PencilAlt } from '@styled-icons/heroicons-outline/PencilAlt'
import { XCircle } from '@styled-icons/heroicons-solid/XCircle'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { ReactComponent as ArrowRotateLeftIcon } from 'assets/ArrowRotateLeftIcon.svg'
import { ReactComponent as DeleteIcon } from 'assets/solid/Delete.svg'
import ActionsAutocompleteFormField from 'components/pricing/Services/edit-service/ActionsAutocompleteFormField'
import ServiceActionTypeChip from 'components/services/ServiceActionTypeChip'
import WorkOrderStatusChip from 'components/work-order/WorkOrderStatusChip'
import {
  clearEditableItem,
  selectEditableItems,
  selectHasItemsMarkedForDeletion,
  selectIsAddingEvent,
  selectItemsMarkedForDeletion,
} from 'data/configured-service/configuredServiceEventsSlice'
import { selectIsOneOff } from 'data/work-order/workOrderSelectors'
import { INVOICE_STATUS } from 'settings/constants/invoice'
import { COMPLETED_WORK_ORDER_STATUSES } from 'settings/constants/service'
import T from 'T'
import { BACKGROUND } from 'theme/colors'
import { normalizeCasing } from 'utils/string'

const ignoreEvent = () => undefined

const EventItem = ({
  event,
  control,
  getAvailableActionsForEvent,
  selectedDate,
  onEdit = ignoreEvent,
  onDelete = ignoreEvent,
  onUndoDelete = ignoreEvent,
  isDraft,
  isPreview = false,
  disposalCountByWorkOrderId = {},
  autocompleteSlotProps,
}) => {
  const dispatch = useDispatch()
  const editableItems = useSelector(selectEditableItems, shallowEqual)
  const itemsMarkedForDeletion = useSelector(selectItemsMarkedForDeletion, shallowEqual)
  const isAddingEvent = useSelector(selectIsAddingEvent, shallowEqual)
  const hasItemsMarkedForDeletion = useSelector(selectHasItemsMarkedForDeletion, shallowEqual)

  const {
    formattedServiceDate,
    workOrderStatus,
    workOrderSequence,
    actionName,
    actionType,
    configuredServiceEventType = '',
    workOrderId,
    invoiceStatus,
    exceptions,
  } = event
  const disposalTicketCount = workOrderId ? disposalCountByWorkOrderId[workOrderId] ?? 0 : 0
  const eventKey = event.id
  const isMarkedForDeletion = itemsMarkedForDeletion[eventKey]
  const isEditable = editableItems[eventKey]
  const formattedEventName = workOrderSequence || normalizeCasing([actionName || actionType, 'event'].filter(Boolean).join(' '))
  const isOneOffEventOrDraft = isDraft || selectIsOneOff({ configuredServiceEventType })
  const handleEdit = useCallback(() => onEdit(event), [onEdit, event])
  const { INVOICED } = INVOICE_STATUS
  let canDelete = true
  let deleteTooltip = T.DELETE_EVENT
  if (COMPLETED_WORK_ORDER_STATUSES.includes(workOrderStatus ?? '')) {
    canDelete = false
    deleteTooltip = T.CANNOT_DELETE_WO_SERVICED_OR_SKIPPED
  } else if (invoiceStatus === INVOICED) {
    canDelete = false
    deleteTooltip = T.CANNOT_DELETE_WO_INVOICED
  } else if (disposalTicketCount > 0 || (exceptions?.length ?? 0) > 0) {
    canDelete = false
    deleteTooltip = T.CANNOT_DELETE_WO_HAS_DISPOSAL_OR_EXCEPTIONS
  }

  return (
    <ListItem
      sx={{
        width: '100%',
        ...(isMarkedForDeletion && {
          backgroundColor: theme => alpha(theme.palette.error.light, 0.3),
        }),
        px: 1,
        py: isPreview ? 1 : 'inherit',
      }}
    >
      <Stack sx={{ width: '100%' }} spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="h5" fontWeight={500}>
            {formattedEventName}
          </Typography>
          {!workOrderId && (
            <Chip
              label={T.UNASSIGNED}
              variant="outlined"
              sx={{ background: BACKGROUND.default, borderColor: 'border.light' }}
              size="small"
              avatar={<Avatar>U</Avatar>}
            />
          )}
          {workOrderStatus && <WorkOrderStatusChip status={workOrderStatus} workOrderId={workOrderId} />}
        </Stack>
        <Typography variant="body2" color="textSecondary" fontWeight={400}>
          {formattedServiceDate}
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1} flexGrow={1} sx={{ py: 0.5 }}>
            {isEditable && !isMarkedForDeletion && (
              <ActionsAutocompleteFormField
                sx={{ width: '100%', mt: '8px !important' }}
                name={`events.${eventKey}.action`}
                control={control}
                options={getAvailableActionsForEvent(event, selectedDate) || []}
                slotProps={autocompleteSlotProps}
              />
            )}
            {(!isEditable || isMarkedForDeletion) && actionType && <ServiceActionTypeChip action={actionType} size="small" />}
          </Stack>
          {isMarkedForDeletion && (
            <Chip label={T.PENDING_DELETION} color="error" variant="outlined" size="small" sx={{ borderRadius: 1 }} />
          )}
          {!isPreview && (
            <Stack direction="row" alignItems="center" justifyContent="end" gap={1}>
              {!isAddingEvent && (isMarkedForDeletion || !isEditable) && (
                <>
                  {isMarkedForDeletion ? (
                    <Tooltip arrow title={T.UNDO_DELETE}>
                      <IconButton onClick={() => onUndoDelete(event)} size="small">
                        <SvgIcon fontSize="small">
                          <ArrowRotateLeftIcon />
                        </SvgIcon>
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip arrow title={deleteTooltip}>
                      <span>
                        <IconButton disabled={!canDelete} onClick={() => onDelete(event)} size="small">
                          <SvgIcon color={canDelete ? 'error' : 'disabled'} fontSize="small">
                            <DeleteIcon />
                          </SvgIcon>
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
              {!hasItemsMarkedForDeletion && !isEditable && !isAddingEvent && (
                <Tooltip arrow title={!isOneOffEventOrDraft ? T.RECURRING_EVENTS_CANNOT_BE_EDITED : T.EDIT_EVENT}>
                  <span>
                    <IconButton disabled={!isOneOffEventOrDraft} size="small" onClick={handleEdit}>
                      <SvgIcon fontSize="small">
                        <PencilAlt />
                      </SvgIcon>
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {editableItems[eventKey] && !isMarkedForDeletion && (
                <Tooltip arrow title={T.CANCEL_EDIT}>
                  <IconButton onClick={() => dispatch(clearEditableItem(eventKey))} size="small">
                    <SvgIcon fontSize="small">
                      <XCircle />
                    </SvgIcon>
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </ListItem>
  )
}

EventItem.propTypes = {
  event: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  getAvailableActionsForEvent: PropTypes.func.isRequired,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onUndoDelete: PropTypes.func,
  isDraft: PropTypes.bool,
  isPreview: PropTypes.bool,
  disposalCountByWorkOrderId: PropTypes.object,
  autocompleteSlotProps: PropTypes.object,
}

export default EventItem
